import * as React from "react";
import { ChoiceGroup, Icon, IChoiceGroupOption, TooltipHost } from "@fluentui/react";
import { LanguageContext } from "../../../../utilities/LocalizationModule";
import { SaveFormat } from "../enums/SaveFormatEnum";

interface ISaveFormatChoiceProps {
    saveFormat: SaveFormat;
    setSaveFormat: (format: SaveFormat) => void;
    // Whether the current selection contains an email message (the mail body or a
    // dropped .eml). Without one, "Emails as PDF" is indistinguishable from "Keep
    // original formats", so the option is hidden.
    showEmailOption: boolean;
    // Whether the selection contains a convertible non-email item. Without one,
    // "Everything as PDF" is indistinguishable from "Emails as PDF", so the
    // option is hidden.
    showEverythingOption: boolean;
}

// The per-save "Save format" choice — the single control replacing the previous
// "Save as PDF?" toggle plus the hidden interplay with the persisted
// convertEmailToPDF setting. Radio (not toggle/dropdown): three mutually
// exclusive options that should all stay visible, in a form that submits via
// the Save button.
export const SaveFormatChoice = ({ saveFormat, setSaveFormat, showEmailOption, showEverythingOption }: ISaveFormatChoiceProps) => {
    const languageStrings = React.useContext(LanguageContext);

    const options: IChoiceGroupOption[] = React.useMemo(() => {
        const result: IChoiceGroupOption[] = [
            { key: String(SaveFormat.Original), text: languageStrings.SaveFormatOriginal },
        ];
        if (showEmailOption) {
            result.push({ key: String(SaveFormat.EmailAsPdf), text: languageStrings.SaveFormatEmailAsPdf });
        }
        if (showEverythingOption) {
            result.push({
                key: String(SaveFormat.EverythingAsPdf),
                text: languageStrings.SaveFormatEverythingAsPdf,
                // The info tooltip rides on this option — it explains the
                // "(where possible)" caveat the label used to carry: only
                // supported types convert, the rest keep their format.
                // defaultRender keeps Fluent's labelled span (id/aria intact);
                // the inline-flex wrapper centers the icon against the text.
                onRenderLabel: (option, defaultRender) => (
                    <span className="xb-save-format-option-label">
                        {defaultRender?.(option)}
                        <TooltipHost content={languageStrings.SaveFormatInfo}>
                            <Icon
                                iconName="Info"
                                className="xb-save-format-info-icon"
                                tabIndex={0}
                                aria-label={languageStrings.SaveFormatInfo}
                            />
                        </TooltipHost>
                    </span>
                )
            });
        }
        return result;
    }, [languageStrings, showEmailOption, showEverythingOption]);

    // A hidden option can still be the active format (persisted default, or an
    // override kept from a previous selection). Each hidden option is
    // outcome-identical to the next option down for the current selection, so
    // walk the highlight down until it lands on a visible option. Display only —
    // the underlying override (and the flags it sends) is untouched.
    let displayFormat = saveFormat;
    if (!showEverythingOption && displayFormat === SaveFormat.EverythingAsPdf) {
        displayFormat = SaveFormat.EmailAsPdf;
    }
    if (!showEmailOption && displayFormat === SaveFormat.EmailAsPdf) {
        displayFormat = SaveFormat.Original;
    }
    const selectedKey = String(displayFormat);

    return (
        <ChoiceGroup
            className="xb-save-format-choice"
            label={languageStrings.SaveFormat}
            selectedKey={selectedKey}
            options={options}
            onChange={(_ev, option) => {
                if (option) {
                    setSaveFormat(Number(option.key) as SaveFormat);
                }
            }}
        />
    );
};
